<script context="module">
  import FolderBreadcrumbs from "./components/FolderBreadcrumbs.svelte";
  export interface IFolderData {
    id: number;
    parentFolderId: number | null;
    folders: {
      id: number;
      name: string;
      containsNewFiles: boolean;
    }[];
    files: {
      name: string;
      url: string;
      isNew: boolean;
    }[];
    breadcrumbs: {
      folderId: number | null;
      name: string;
      isDisabled: boolean;
    }[];
  }
</script>

<script>
  import { createEventDispatcher, onMount } from "svelte";
  import axios, { AxiosResponse } from "axios";
  import { t } from "../../../../core";

  const dispatch = createEventDispatcher();

  let folderData: IFolderData;
  let isLoading: boolean = true;
  let isSearchActive: boolean;

  onMount(() => {
    getFolder();
  });

  export function getFolder(folderId?: number | null, search?: string) {
    isLoading = true;

    if (search) {
      isSearchActive = true;

      axios
        .post("/api/ConstructionFolder/search", { input: search })
        .then((res: AxiosResponse<IFolderData>) => {
          folderData = res.data;
          isLoading = false;
        })
        .catch(() => (isLoading = false));
    } else {
      const apiString = folderId
        ? `/api/ConstructionFolder?folderId=${folderId}`
        : `/api/ConstructionFolder`;

      axios
        .get(apiString)
        .then((res: AxiosResponse<IFolderData>) => {
          folderData = res.data;
          isSearchActive = false;
          isLoading = false;
        })
        .catch(() => (isLoading = false));

      dispatch("clearSearch");
    }
  }

  function handleBack() {
    if (folderData?.breadcrumbs.length > 1) {
      const parentCategory =
        folderData?.breadcrumbs[folderData?.breadcrumbs.length - 2];
      getFolder(parentCategory.folderId);
    }
  }

  function downloadFiles() {
    window.location.href =
      "/api/constructionFile/downloadFilesArchive/documents.zip";
  }
</script>

{#if folderData}
  <div class="folder-wrapper">
    <div class="folder">
      <FolderBreadcrumbs
        {isSearchActive}
        {folderData}
        {handleBack}
        {getFolder}
      />
      <table
        class="folder__table"
      >
        <thead>
          <tr>
            <th class="folder__table-type">{t("Page.DocLib.Type")}</th>
            <th class="folder__table-title">{t("Page.DocLib.Title")}</th>
          </tr>
        </thead>
        <tbody>
          {#if !isLoading && !folderData.folders?.length && !folderData.files?.length}
            <tr>
              <td></td>
              <td>
                {t("Page.DocLib.NoItems")}
              </td>
            </tr>
          {:else}
            {#each folderData.folders as folder}
              <tr on:click={() => getFolder(folder.id)}>
                <td>
                  <div class="icon-wrapper">
                    {#if folderData.id === null}
                      <svg
                        class="icon"
                        width="20"
                        height="20"
                        aria-label="folder"
                      >
                        <use xlink:href="#folder" />
                      </svg>
                    {:else}
                      <svg
                        class="icon"
                        width="28"
                        height="20"
                        aria-label="folder-sub"
                      >
                        <use xlink:href="#folder-sub" />
                      </svg>
                    {/if}
  
                    {#if folder.containsNewFiles}
                      <span class="item-badge">{t("Item.Document.New")}</span>
                    {/if}
                  </div>
                </td>
                <td><span class="link">{folder.name}</span></td>
              </tr>
            {/each}
          {/if}

          {#each folderData.files as file}
            <tr>
              <td>
                <div class="icon-wrapper">
                  <svg
                    class="icon"
                    width="20"
                    height="24"
                    aria-label="document"
                  >
                    <use xlink:href="#document" />
                  </svg>

                  {#if file.isNew}
                    <span class="item-badge">{t("Item.Document.New")}</span>
                  {/if}
                </div>
              </td>
              <td>
                <a
                  href={file.url}
                  class="link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {file.name}
                </a>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>

    <div class="folder__download-all">
      <button
        on:click={downloadFiles}
        class="btn"
        disabled={folderData.id === null &&
          folderData.files.length === 0 &&
          folderData.folders.length === 0}
      >
        {t("Page.DocLib.DownloadAllAsZip")}
      </button>
    </div>
  </div>
{/if}

<style lang="scss">
  .folder-wrapper {
    display: grid;
  }

  .folder {
    width: 100%;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    border: 1px solid $separator-color;
    padding: 15px;
    background: $text-reverse-color;
    margin-bottom: 15px;

    &__table {
      width: 100%;

      &.empty {
        padding-bottom: 30px;
      }

      td,
      th {
        width: 99%;

        &:first-child {
          width: 100px;
          white-space: nowrap;

          @include sm {
            min-width: 100px;
          }
        }
      }

      th {
        border-bottom: 1px solid $separator-color;
        padding: 3px 10px;
      }

      td {
        vertical-align: top;
        padding: 6px 10px;
      }

      tbody tr:first-child td {
        padding-top: 14px;
      }
    }

    &__table-title {
      width: 90%;
      @include sm {
        width: 60%;
      }
    }

    &__table-type {
      @include sm {
        width: 100px;
      }
    }
  }

  .icon {
    fill: $key-color;

    &-wrapper {
      width: fit-content;
      position: relative;
      line-height: 1;
    }
  }

  .item-badge {
    background: $warning-color;
    color: $text-reverse-color;
    font-size: 10px;
    display: inline-block;
    padding: 2px 4px;
    line-height: 1;
    position: absolute;
    top: calc(100% - 3px);
    right: 0;
    transform: translate(50%, -50%);
  }

  .link {
    word-break: break-word;
  }
</style>
